const API_URL = process.env.REACT_APP_BACKEND_URL;

// In-memory token fallback for browsers that block storage (WhatsApp WebView, etc.)
let inMemoryToken = null;

// Get token from multiple sources with fallbacks
// Primary: httpOnly cookie (handled automatically by browser with credentials: 'include')
// Fallback: in-memory token or storage for restricted environments
const getToken = () => {
  // Try in-memory first (most reliable for restricted browsers)
  if (inMemoryToken) {
    return inMemoryToken;
  }
  
  // Then try storage APIs (fallback only)
  try {
    const localToken = localStorage.getItem('texhub_token');
    if (localToken) return localToken;
  } catch (e) {
    // localStorage not available
  }
  
  try {
    const sessionToken = sessionStorage.getItem('texhub_token');
    if (sessionToken) return sessionToken;
  } catch (e) {
    // sessionStorage not available
  }
  
  return null;
};

// Set token with multiple fallbacks
// Note: Primary auth uses httpOnly cookies, this is fallback for restricted browsers
export const setApiToken = (token) => {
  inMemoryToken = token;
  
  // Storage is fallback only - cookies are primary
  try {
    if (token) {
      localStorage.setItem('texhub_token', token);
    } else {
      localStorage.removeItem('texhub_token');
    }
  } catch (e) {
    // localStorage not available
  }
  
  try {
    if (token) {
      sessionStorage.setItem('texhub_token', token);
    } else {
      sessionStorage.removeItem('texhub_token');
    }
  } catch (e) {
    // sessionStorage not available
  }
};

// Clear all tokens
export const clearApiToken = () => {
  inMemoryToken = null;
  try { localStorage.removeItem('texhub_token'); } catch (e) {}
  try { sessionStorage.removeItem('texhub_token'); } catch (e) {}
};

// Build headers - token is optional as cookies are primary auth mechanism
const getAuthHeaders = (customToken = null) => {
  const token = customToken || getToken();
  // Get region from localStorage
  let region = 'MX';
  try {
    region = localStorage.getItem('texhub_region') || 'MX';
  } catch (e) {}
  
  const headers = {
    'Content-Type': 'application/json',
    'X-User-Region': region
  };
  
  // Add Authorization header as fallback (cookies are primary)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Fetch wrapper that includes credentials for cookie-based auth
export const fetchWithCredentials = (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include', // Include httpOnly cookies in requests
  });
};

const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = 'Error en la solicitud';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
      
      // Handle specific error codes
      if (response.status === 401) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión de nuevo.';
        clearApiToken();
      }
    } catch (e) {
      // If we can't parse the response, use generic message
    }
    const err = new Error(errorMessage);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

// User Preferences
export const updateUserPreferences = (data) => {
  return fetchWithCredentials(`${API_URL}/api/auth/preferences`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  }).then(handleResponse);
};

// Companies
export const getCompanies = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithCredentials(`${API_URL}/api/companies?${queryString}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const getCompany = (companyId) => {
  return fetchWithCredentials(`${API_URL}/api/companies/${companyId}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const getMyCompany = () => {
  return fetchWithCredentials(`${API_URL}/api/my-company`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const createCompany = (data) => {
  return fetchWithCredentials(`${API_URL}/api/companies`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const updateCompany = (companyId, data) => {
  return fetchWithCredentials(`${API_URL}/api/companies/${companyId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  }).then(handleResponse);
};

// Products
export const getProducts = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithCredentials(`${API_URL}/api/products?${queryString}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const getProduct = (productId) => {
  return fetchWithCredentials(`${API_URL}/api/products/${productId}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const getMyProducts = () => {
  return fetchWithCredentials(`${API_URL}/api/my-products`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const createProduct = (data) => {
  return fetchWithCredentials(`${API_URL}/api/products`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const updateProduct = (productId, data) => {
  return fetchWithCredentials(`${API_URL}/api/products/${productId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const deleteProduct = (productId) => {
  return fetchWithCredentials(`${API_URL}/api/products/${productId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

// Store Products Search (Public)
export const searchStoreProducts = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithCredentials(`${API_URL}/api/stores/products/search?${queryString}`).then(handleResponse);
};

// Search Stores (Companies with online stores)
export const searchStores = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithCredentials(`${API_URL}/api/stores/search?${queryString}`).then(handleResponse);
};

// Quotes
export const getQuotes = (type = 'received') => {
  return fetchWithCredentials(`${API_URL}/api/quotes?type=${type}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getQuote = (quoteId) => {
  return fetchWithCredentials(`${API_URL}/api/quotes/${quoteId}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Alias for getQuote
export const getQuoteDetail = getQuote;

export const createQuote = (data) => {
  return fetchWithCredentials(`${API_URL}/api/quotes`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const respondToQuote = (quoteId, data) => {
  return fetchWithCredentials(`${API_URL}/api/quotes/${quoteId}/respond`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const getQuoteResponses = (quoteId) => {
  return fetchWithCredentials(`${API_URL}/api/quotes/${quoteId}/responses`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const updateQuoteStatus = (quoteId, status) => {
  return fetchWithCredentials(`${API_URL}/api/quotes/${quoteId}/status?status=${status}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Samples
export const getSamples = (type = 'received') => {
  return fetchWithCredentials(`${API_URL}/api/samples?type=${type}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getSample = (sampleId) => {
  return fetchWithCredentials(`${API_URL}/api/samples/${sampleId}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const createSample = (data) => {
  return fetchWithCredentials(`${API_URL}/api/samples`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const updateSampleStatus = (sampleId, status, sampleCost, shippingCost) => {
  const params = new URLSearchParams({ status });
  if (sampleCost !== undefined) params.append('sample_cost', sampleCost);
  if (shippingCost !== undefined) params.append('shipping_cost', shippingCost);
  
  return fetchWithCredentials(`${API_URL}/api/samples/${sampleId}/status?${params}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Import Programs
export const getImports = (type = 'all') => {
  return fetchWithCredentials(`${API_URL}/api/imports?type=${type}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const createImport = (data) => {
  return fetchWithCredentials(`${API_URL}/api/imports`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const updateImportStatus = (importId, status) => {
  return fetchWithCredentials(`${API_URL}/api/imports/${importId}/status?status=${status}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Messages
export const getMessages = (type = 'inbox') => {
  return fetchWithCredentials(`${API_URL}/api/messages?type=${type}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const sendMessage = (data) => {
  return fetchWithCredentials(`${API_URL}/api/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const markMessageRead = (messageId) => {
  return fetchWithCredentials(`${API_URL}/api/messages/${messageId}/read`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Conversations
export const getConversations = () => {
  return fetchWithCredentials(`${API_URL}/api/conversations`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getConversation = (conversationId) => {
  return fetchWithCredentials(`${API_URL}/api/conversations/${conversationId}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const createConversation = (data) => {
  return fetchWithCredentials(`${API_URL}/api/conversations`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const sendConversationMessage = (conversationId, data) => {
  return fetchWithCredentials(`${API_URL}/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const getUnreadConversationsCount = () => {
  return fetchWithCredentials(`${API_URL}/api/conversations/unread/count`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Orders
export const getOrders = (type = 'received') => {
  return fetchWithCredentials(`${API_URL}/api/orders?type=${type}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const createOrder = (data) => {
  return fetchWithCredentials(`${API_URL}/api/orders`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const getOrder = (orderId) => {
  return fetchWithCredentials(`${API_URL}/api/orders/${orderId}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const updateOrderStatus = (orderId, status) => {
  return fetchWithCredentials(`${API_URL}/api/orders/${orderId}/status?status=${status}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Notifications
export const getNotifications = () => {
  return fetchWithCredentials(`${API_URL}/api/notifications`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const markNotificationRead = (notificationId) => {
  return fetchWithCredentials(`${API_URL}/api/notifications/${notificationId}/read`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const markAllNotificationsRead = () => {
  return fetchWithCredentials(`${API_URL}/api/notifications/read-all`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getUnreadCount = () => {
  return fetchWithCredentials(`${API_URL}/api/notifications/unread-count`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Notification Preferences
export const getNotificationPreferences = () => {
  return fetchWithCredentials(`${API_URL}/api/notifications/preferences`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const updateNotificationPreferences = (preferences) => {
  return fetchWithCredentials(`${API_URL}/api/notifications/preferences`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ preferences })
  }).then(handleResponse);
};

// Subscriptions
export const getPlans = (lang = 'es') => {
  return fetchWithCredentials(`${API_URL}/api/subscriptions/plans?lang=${lang}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const createCheckout = (planId, billingCycle) => {
  const originUrl = window.location.origin;
  return fetchWithCredentials(`${API_URL}/api/checkout/session`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify({ 
      plan_id: planId, 
      billing_cycle: billingCycle,
      origin_url: originUrl
    })
  }).then(handleResponse);
};

export const getCheckoutStatus = (sessionId) => {
  return fetchWithCredentials(`${API_URL}/api/checkout/status/${sessionId}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Saved Suppliers
export const getSavedSuppliers = () => {
  return fetchWithCredentials(`${API_URL}/api/saved-suppliers`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const saveSupplier = (companyId) => {
  return fetchWithCredentials(`${API_URL}/api/saved-suppliers/${companyId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const removeSavedSupplier = (companyId) => {
  return fetchWithCredentials(`${API_URL}/api/saved-suppliers/${companyId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Favorite Products
export const getFavoriteProducts = () => {
  return fetchWithCredentials(`${API_URL}/api/favorites/products`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const addFavoriteProduct = (productId) => {
  return fetchWithCredentials(`${API_URL}/api/favorites/products/${productId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const removeFavoriteProduct = (productId) => {
  return fetchWithCredentials(`${API_URL}/api/favorites/products/${productId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const checkFavoriteStatus = (productId) => {
  return fetchWithCredentials(`${API_URL}/api/favorites/products/${productId}/status`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

// Store Metrics
export const getStoreMetrics = (period = 30) => {
  return fetchWithCredentials(`${API_URL}/api/stores/my/metrics?period=${period}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const recordStoreView = (storeId) => {
  return fetchWithCredentials(`${API_URL}/api/stores/${storeId}/view`, {
    method: 'POST',
  }).then(handleResponse);
};

export const recordProductView = (productId) => {
  return fetchWithCredentials(`${API_URL}/api/stores/products/${productId}/view`, {
    method: 'POST',
  }).then(handleResponse);
};

// Categories

// Translate product text
export const translateProduct = (name, description) => {
  return fetchWithCredentials(`${API_URL}/api/translate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name, description })
  }).then(handleResponse);
};

export const getCategories = (lang = 'es') => {
  return fetchWithCredentials(`${API_URL}/api/categories?lang=${lang}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Dashboard Stats
export const getProviderStats = () => {
  return fetchWithCredentials(`${API_URL}/api/dashboard/provider-stats`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getManufacturerStats = () => {
  return fetchWithCredentials(`${API_URL}/api/dashboard/manufacturer-stats`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Admin
export const getAdminStats = () => {
  return fetchWithCredentials(`${API_URL}/api/admin/stats`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getAdminUsers = () => {
  return fetchWithCredentials(`${API_URL}/api/admin/users`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const verifyCompany = (companyId, verified) => {
  return fetchWithCredentials(`${API_URL}/api/admin/companies/${companyId}/verify?verified=${verified}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const featureCompany = (companyId, featured) => {
  return fetchWithCredentials(`${API_URL}/api/admin/companies/${companyId}/feature?featured=${featured}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const updateUserRole = (userId, role) => {
  return fetchWithCredentials(`${API_URL}/api/admin/users/${userId}/role?role=${role}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// TexVision AI
export const createTexVisionRender = (data) => {
  return fetchWithCredentials(`${API_URL}/api/texvision/render`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const getTexVisionRenders = () => {
  return fetchWithCredentials(`${API_URL}/api/texvision/renders`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// Surplus/Excedentes
export const getSurplusList = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithCredentials(`${API_URL}/api/surplus?${queryString}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getFeaturedSurplus = (limit = 6) => {
  return fetchWithCredentials(`${API_URL}/api/surplus/featured?limit=${limit}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getSurplusTypes = (lang = 'es') => {
  return fetchWithCredentials(`${API_URL}/api/surplus/types?lang=${lang}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getMySurplus = (status = null) => {
  const params = status ? `?status=${status}` : '';
  return fetchWithCredentials(`${API_URL}/api/surplus/my${params}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getSurplus = (surplusId) => {
  return fetchWithCredentials(`${API_URL}/api/surplus/${surplusId}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const createSurplus = (data) => {
  return fetchWithCredentials(`${API_URL}/api/surplus`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const updateSurplus = (surplusId, data) => {
  return fetchWithCredentials(`${API_URL}/api/surplus/${surplusId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const deleteSurplus = (surplusId) => {
  return fetchWithCredentials(`${API_URL}/api/surplus/${surplusId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const createSurplusInquiry = (surplusId) => {
  return fetchWithCredentials(`${API_URL}/api/surplus/${surplusId}/inquiry`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const saveSurplus = (surplusId) => {
  return fetchWithCredentials(`${API_URL}/api/surplus/${surplusId}/save`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getSavedSurplus = () => {
  return fetchWithCredentials(`${API_URL}/api/surplus/saved/list`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const checkSurplusSaved = (surplusId) => {
  return fetchWithCredentials(`${API_URL}/api/surplus/${surplusId}/is-saved`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

// ==================== RATINGS/REVIEWS ====================

export const getRatingCriteria = () => {
  return fetchWithCredentials(`${API_URL}/api/ratings/criteria`, {
    
  }).then(handleResponse);
};

export const createRating = (data) => {
  return fetchWithCredentials(`${API_URL}/api/ratings`, {
    method: 'POST',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const getCompanyRatings = (companyId, ratingType = null, skip = 0, limit = 20) => {
  const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
  if (ratingType) params.append('rating_type', ratingType);
  return fetchWithCredentials(`${API_URL}/api/ratings/company/${companyId}?${params}`, {
    
  }).then(handleResponse);
};

export const getCompanyRatingSummary = (companyId, ratingType = 'provider') => {
  return fetchWithCredentials(`${API_URL}/api/ratings/company/${companyId}/summary?rating_type=${ratingType}`, {
    
  }).then(handleResponse);
};

export const updateRating = (ratingId, data) => {
  return fetchWithCredentials(`${API_URL}/api/ratings/${ratingId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const deleteRating = (ratingId) => {
  return fetchWithCredentials(`${API_URL}/api/ratings/${ratingId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const checkExistingRating = (targetCompanyId, ratingType) => {
  return fetchWithCredentials(`${API_URL}/api/ratings/check/${targetCompanyId}?rating_type=${ratingType}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getMyGivenRatings = (skip = 0, limit = 50) => {
  return fetchWithCredentials(`${API_URL}/api/ratings/my-given?skip=${skip}&limit=${limit}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};

export const getMyReceivedRatings = (ratingType = null, skip = 0, limit = 50) => {
  const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
  if (ratingType) params.append('rating_type', ratingType);
  return fetchWithCredentials(`${API_URL}/api/ratings/my-received?${params}`, {
    headers: getAuthHeaders(),
    
  }).then(handleResponse);
};



// ==================== INVENTORY ====================
export const getProductInventory = (productId) => {
  return fetchWithCredentials(`${API_URL}/api/stores/products/${productId}/inventory`).then(handleResponse);
};

export const addProductVariant = (productId, variant) => {
  return fetchWithCredentials(`${API_URL}/api/stores/my/products/${productId}/variants`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(variant)
  }).then(handleResponse);
};

export const updateProductVariant = (productId, variantId, data) => {
  return fetchWithCredentials(`${API_URL}/api/stores/my/products/${productId}/variants/${variantId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  }).then(handleResponse);
};

export const deleteProductVariant = (productId, variantId) => {
  return fetchWithCredentials(`${API_URL}/api/stores/my/products/${productId}/variants/${variantId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

// ==================== SHOPPING CART ====================
export const getCart = (storeId) => {
  return fetchWithCredentials(`${API_URL}/api/cart/${storeId}`).then(handleResponse);
};

export const addToCart = (storeId, item) => {
  return fetchWithCredentials(`${API_URL}/api/cart/${storeId}/add`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item)
  }).then(handleResponse);
};

export const updateCartItem = (storeId, item) => {
  return fetchWithCredentials(`${API_URL}/api/cart/${storeId}/update`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(item)
  }).then(handleResponse);
};

export const clearCart = (storeId) => {
  return fetchWithCredentials(`${API_URL}/api/cart/${storeId}`, {
    method: 'DELETE',
  }).then(handleResponse);
};

// ==================== STORE ORDERS ====================
export const createStoreOrder = (storeId, orderData) => {
  return fetchWithCredentials(`${API_URL}/api/stores/${storeId}/orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData)
  }).then(handleResponse);
};

export const getMyStoreOrders = () => {
  return fetchWithCredentials(`${API_URL}/api/stores/my/orders`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const updateStoreOrderStatus = (orderId, status) => {
  return fetchWithCredentials(`${API_URL}/api/stores/my/orders/${orderId}/status?status=${status}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

// Customer order endpoints
export const getMyOrders = () => {
  return fetchWithCredentials(`${API_URL}/api/my-orders`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const getMyOrderDetail = (orderId) => {
  return fetchWithCredentials(`${API_URL}/api/my-orders/${orderId}`, {
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const cancelMyOrder = (orderId) => {
  return fetchWithCredentials(`${API_URL}/api/my-orders/${orderId}/cancel`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  }).then(handleResponse);
};

export const updateMyOrder = (orderId, updates) => {
  return fetchWithCredentials(`${API_URL}/api/my-orders/${orderId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  }).then(handleResponse);
};
